import { Authenticated, useInvalidate, useList } from "@refinedev/core";
import { QuotaFeatures } from "@/constants";
import { useSession } from "hooks/useSession";
import { useQuota } from "hooks/useQuota";
import { useWorkspace } from "hooks/useWorkspace";
import { useCallback, useEffect, useMemo } from "react";
import { MediaProjectListItem } from "../types";
import { LoadingSpinner } from "components/LoadingSpinner/";
import { useProjectWizardStore } from "../projects/useProjectWizardStore";
import { OnboardingDashboard } from "./components/OnboardingDashboard";
import { WelcomeHeader } from "./components/WelcomeHeader";
import { ActionCards } from "./components/ActionCards";
import { RecentProjects } from "./components/RecentProjects";
import { SoftPaywallModal } from "components/SoftPaywallModal";
import { useSoftPaywall } from "@/providers/SoftPaywallProvider";
import styled from "styled-components";

export const HomePage = () => {
  const { currentOrganization, user } = useSession();

  const {
    paywallFlow,
    showSoftPaywall,
    experiencesLeft,
    isTrialExpired,
    openSoftPaywall,
    dismissSoftPaywall,
  } = useSoftPaywall();

  // Proactively show soft paywall for hard_no_card_3d users on days 2-3
  // or when trial is expired (all free experiences used)
  useEffect(() => {
    if (isTrialExpired) {
      openSoftPaywall();
      return;
    }
    if (paywallFlow !== "hard_no_card_3d") return;
    if (!user?.created) return;
    const days =
      (Date.now() - new Date(user.created).getTime()) / (1000 * 60 * 60 * 24);
    if (days >= 2 && days < 3) {
      openSoftPaywall();
    }
  }, [isTrialExpired, paywallFlow, user?.created, openSoftPaywall]);

  const { workspaceId } = useWorkspace();
  const { isBlocked, isNearLimit } = useQuota();

  const isProjectQuotaReached =
    isBlocked(QuotaFeatures.MediaProject) || isTrialExpired;
  const isNearProjectQuota = isNearLimit(QuotaFeatures.MediaProject);
  const { setHasNoProjects } = useProjectWizardStore();
  const invalidate = useInvalidate();

  const projectListResource = useMemo(() => {
    const base = `media/${currentOrganization?.id}/projects`;
    return workspaceId ? `${base}?workspace_id=${workspaceId}` : base;
  }, [currentOrganization?.id, workspaceId]);

  const { data: projectsData, isLoading } = useList<MediaProjectListItem>({
    resource: projectListResource,
    queryOptions: {
      enabled: Boolean(currentOrganization?.id),
    },
    pagination: {
      pageSize: 3,
    },
    sorters: [{ field: "created", order: "desc" }],
  });

  const handleDeleteSuccess = useCallback(() => {
    return invalidate({
      resource: projectListResource,
      invalidates: ["list"],
    });
  }, [invalidate, projectListResource]);

  const hasNoProjects = projectsData?.data.length === 0;

  useEffect(() => {
    if (projectsData !== undefined) {
      setHasNoProjects(hasNoProjects);
    }
  }, [hasNoProjects, setHasNoProjects, projectsData]);

  if (isLoading || !currentOrganization?.id) return <LoadingSpinner />;

  if (hasNoProjects) {
    return (
      <Authenticated key="home">
        <OnboardingDashboard showWelcomeModal={true} />
      </Authenticated>
    );
  }

  return (
    <Authenticated key="home">
      <HomePageWrapper data-testid="home-page">
        <WelcomeHeader isProjectQuotaReached={isProjectQuotaReached} />
        <ActionCards
          isProjectQuotaReached={isProjectQuotaReached}
          isNearQuota={isNearProjectQuota}
        />
        <RecentProjects
          projects={projectsData?.data}
          isLoading={isLoading}
          onDeleteSuccess={handleDeleteSuccess}
          isProjectQuotaReached={isProjectQuotaReached}
        />
      </HomePageWrapper>
      <SoftPaywallModal
        open={showSoftPaywall}
        onClose={dismissSoftPaywall}
        experiencesLeft={experiencesLeft}
        paywallFlow={paywallFlow}
      />
    </Authenticated>
  );
};

const HomePageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 24px 24px 24px 40px;
  min-height: 100vh;
  background-color: transparent;
`;
